import { useMemo, useState } from 'react'

import { Title } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'

import TemplateList from 'modules/documents/components/DocumentTemplates/Content/DocumentTemplatesList/TemplateList'
import filesStore from 'modules/documents/components/Documents/store'
import { useGetDocumentTemplates } from 'modules/documents/hooks/useGetDocumentTemplates'
import { useTaskConfigurationSendDocumentTemplate } from 'modules/processes/hooks/useTaskConfigurationSendDocumentTemplate'

import CreateModal from './CreateModal'

interface Props {
  onCreateModalOpen: () => void
}

const TemplateLists = ({ onCreateModalOpen }: Props) => {
  const { loading, documentTemplates } = useGetDocumentTemplates()

  const allTemplateIds = useMemo(
    () => documentTemplates.map((template) => String(template.id!)),
    [documentTemplates]
  )

  const { taskConfigurations } = useTaskConfigurationSendDocumentTemplate(allTemplateIds)

  const [systemTemplates, companyTemplates] = useMemo(() => {
    const systemTemplates = documentTemplates.filter((template) => template.systemTemplate)
    const companyTemplates = documentTemplates.filter((template) => !template.systemTemplate)
    return [systemTemplates, companyTemplates]
  }, [documentTemplates])

  if (loading) return <Loading />

  return (
    <>
      <TemplateList
        key="systemTemplates"
        templates={systemTemplates}
        taskConfigurations={taskConfigurations}
        title={i18n.t('documents.document_templates.list.system_templates')}
      />
      <TemplateList
        key="companyTemplates"
        templates={companyTemplates}
        taskConfigurations={taskConfigurations}
        title={i18n.t('documents.document_templates.list.company_templates')}
        showEmptyList
        addButton={{
          label: i18n.t('documents.document_templates.list.new'),
          onClick: onCreateModalOpen
        }}
      />
    </>
  )
}

const DocumentTemplatesList = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const onCreateModalOpen = () => {
    setIsModalOpen(true)
  }

  const onCreateModalCloses = () => {
    filesStore.setFiles([])
    setIsModalOpen(false)
  }

  return (
    <>
      <Title
        description={i18n.t('documents.document_templates.list.your_templates_description_v2')}
        title=""
        variant="500"
      />
      <HelpCenterLink name="document_templates">
        {i18n.t('documents.document_templates.list.your_templates_learn_more')}
      </HelpCenterLink>
      {isModalOpen && <CreateModal onClose={onCreateModalCloses} />}
      <TemplateLists onCreateModalOpen={onCreateModalOpen} />
    </>
  )
}

export default observer(DocumentTemplatesList)
